import { Link } from 'react-router-dom';
import type { AskSourceAyah } from '@/types/ai';
import { HighlightAllahText } from '@/components/ui/HighlightAllahText';

type Props = {
  sources: AskSourceAyah[];
  /** Citations the model actually used in the answer. */
  citedRefs: Set<string>;
};

/**
 * Sources footer for the Ask page. Lists all retrieved ayahs (up to
 * Phase 4's top-K=8). Ayahs the model actually cited are marked with
 * an accent dot; others sit at lower opacity ("retrieved but not
 * cited"). Each row deep-links to the reader.
 */
export function SourcesList({ sources, citedRefs }: Props) {
  if (sources.length === 0) return null;

  return (
    <section className="mt-12">
      <h2 className="text-xs uppercase tracking-[0.22em] text-ink-500 dark:text-ink-400 mb-4">
        Sources
      </h2>
      <ol className="divide-y divide-ink-200/60 dark:divide-ink-800/60">
        {sources.map((s) => {
          const ref = `${s.surah_id}:${s.ayah_number}`;
          const cited = citedRefs.has(ref);
          return (
            <li key={s.ayah_id}>
              <Link
                to={`/surah/${s.surah_id}/ayah/${s.ayah_number}`}
                className={
                  'block py-4 -mx-3 px-3 rounded-lg transition-colors ' +
                  'hover:bg-ink-100/60 dark:hover:bg-ink-900/40 ' +
                  (cited ? 'opacity-100' : 'opacity-70')
                }
                aria-label={`Surah ${s.surah_id}, Ayah ${s.ayah_number}, opens in reader`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {cited && (
                    <span
                      aria-hidden="true"
                      className="h-1.5 w-1.5 rounded-full bg-accent"
                    />
                  )}
                  <span className="font-serif text-sm text-ink-700 dark:text-ink-200 num-tight">
                    {ref}
                  </span>
                  <span className="text-xs text-ink-400 dark:text-ink-500">
                    {Math.round(s.similarity * 100)}% match
                  </span>
                </div>
                <p
                  dir="rtl"
                  className="font-arabic text-base leading-loose text-ink-800 dark:text-ink-100 mb-1"
                >
                  <HighlightAllahText text={s.text_ar} />
                </p>
                {s.translation_en && (
                  <p className="text-sm leading-relaxed text-ink-500 dark:text-ink-400">
                    <HighlightAllahText text={s.translation_en} />
                  </p>
                )}
              </Link>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
